import React, { useEffect, useMemo, useState } from "react";
import { useDebounce } from "../../hooks/useDebounce";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion } from "framer-motion";
import { FiAlertCircle, FiInbox, FiPackage } from "react-icons/fi";
import Navbar from "../Navbar/Navbar";
import Footer from "../Footer/Footer";
import ProductCardRedesigned from "./ProductCardRedesigned";
import ProductDetailModalRedesigned from "./ProductDetailModalRedesigned";
import ProductCommandBar from "./ProductCommandBar";
import FarmerStockEditor from "./FarmerStockEditor";
import { useAuth } from "../../context/AuthContext";
import { getProducts } from "../../Utils/storeService";
import { updateProduct } from "../../Utils/adminService";
import { formatLek } from "../../Utils/currency";
import { useRecentlyViewed } from "../../hooks/useRecentlyViewed";
import * as P from "./Products.styled";

const DEFAULT_FILTERS = { organic: false, pesticideFree: false };

const ProductList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { userRole, token } = useAuth();
  const { recentlyViewed } = useRecentlyViewed();
  const [products, setProducts] = useState([]);
  const [selected, setSelected] = useState(null);
  const [edit, setEdit] = useState(null);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 300);
  const [sort, setSort] = useState("recommended");
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [viewMode, setViewMode] = useState("grid");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const canEditProducts = userRole === "SuperAdmin";

  useEffect(() => {
    let active = true;
    const loadProducts = async () => {
      try {
        const data = await getProducts();
        if (!active) return;
        const mapped = (data || []).map((product) => {
          const description = product.description || "";
          const badges = [];
          if (/organic/i.test(description)) badges.push("Organic");
          if (/pesticide/i.test(description)) badges.push("Pesticide-Free");
          return {
            id: product.id,
            farmId: product.farmId,
            farmName: product.farmName || "",
            name: product.name,
            description,
            harvestDate: null,
            location: null,
            badges,
            season: null,
            stock: Number(product.stock || 0),
            price: Number(product.price || 0),
            // Plumb the sales signal so the "Popular" badge can actually fire.
            orderCount: Number(product.orderCount || product.salesCount || 0),
            traceabilityId: `PRD-${product.id}`,
            unit: product.unit || "",
            image:
              product.imageUrl ||
              `data:image/svg+xml,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="300" height="200" fill="%231a1a2e"><rect width="300" height="200"/><text x="150" y="105" fill="%23666" font-size="14" text-anchor="middle">${product.name || "Product"}</text></svg>`)}`,
            imageUrl: product.imageUrl || null,
            isActive: true,
          };
        });
        setProducts(mapped);
      } catch (err) {
        console.error(err);
        if (active) setError(t("products.load_error", "Failed to load products."));
      } finally {
        if (active) setLoading(false);
      }
    };

    loadProducts();
    return () => {
      active = false;
    };
  }, [t]);

  const filtered = useMemo(() => {
    let result = products.filter((p) => {
      if (filters.organic && !p.badges.includes("Organic")) return false;
      if (filters.pesticideFree && !p.badges.includes("Pesticide-Free")) return false;
      return p.name.toLowerCase().includes(debouncedSearch.toLowerCase());
    });

    switch (sort) {
      case "price-asc":
        result = [...result].sort((a, b) => a.price - b.price);
        break;
      case "price-desc":
        result = [...result].sort((a, b) => b.price - a.price);
        break;
      case "name-asc":
        result = [...result].sort((a, b) => a.name.localeCompare(b.name));
        break;
      default:
        break;
    }

    return result;
  }, [products, filters, debouncedSearch, sort]);

  const handleSave = async (updated) => {
    if (!token) {
      setError(t("products.admin_required", "Please log in as admin to edit products."));
      return;
    }
    try {
      const payload = {
        farmId: Number(updated.farmId),
        name: updated.name,
        description: updated.description || null,
        unit: updated.unit || null,
        price: Number(updated.price),
        stock: Number(updated.stock),
        imageUrl: updated.imageUrl || null,
        isActive: updated.isActive !== false,
      };
      const serverProduct = await updateProduct(token, updated.id, payload);
      setProducts((items) =>
        items.map((item) =>
          item.id === updated.id
            ? {
                ...item,
                ...updated,
                price: Number(serverProduct.price || updated.price),
                stock: Number(serverProduct.stock || updated.stock),
                unit: serverProduct.unit || updated.unit,
                imageUrl: serverProduct.imageUrl || updated.imageUrl,
              }
            : item
        )
      );
      setEdit(null);
    } catch (err) {
      console.error(err);
      setError(t("products.update_error", "Failed to update product."));
    }
  };

  const handleToggleFilter = (key) => {
    setFilters((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleResetAll = () => {
    setFilters(DEFAULT_FILTERS);
    setSearch("");
    setSort("recommended");
  };

  const hasActiveFilters =
    Boolean(search) || filters.organic || filters.pesticideFree;

  const skeletonCount = 8;

  return (
    <P.Page>
      <Navbar />
      <P.Container>
        <P.PageHeader>
          <P.PageHeading>
            <P.Kicker>
              <FiPackage
                aria-hidden="true"
                style={{ marginRight: 6, verticalAlign: "-2px" }}
              />
              {t("products.kicker", { defaultValue: "Fresh today" })}
            </P.Kicker>
            <P.Title>
              {t("products.page_title", { defaultValue: "All products" })}
            </P.Title>
            <P.Subtitle>
              {t("products.page_subtitle", {
                defaultValue:
                  "Farm-fresh produce delivered straight from Albanian farms. Browse, filter, and add to your cart.",
              })}
            </P.Subtitle>
          </P.PageHeading>
          {!loading ? (
            <P.HeaderMeta>
              <strong>{products.length}</strong>{" "}
              {t("products.total_count", {
                defaultValue: "total products",
              })}
            </P.HeaderMeta>
          ) : null}
        </P.PageHeader>

        <ProductCommandBar
          search={search}
          onSearchChange={setSearch}
          filters={filters}
          onToggleFilter={handleToggleFilter}
          sort={sort}
          onSortChange={setSort}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          resultCount={filtered.length}
          loading={loading}
          onResetAll={handleResetAll}
        />

        {error ? (
          <P.ErrorBanner role="alert">
            <FiAlertCircle size={20} aria-hidden="true" />
            {error}
          </P.ErrorBanner>
        ) : null}

        {loading ? (
          <P.Grid aria-busy="true" aria-live="polite">
            {Array.from({ length: skeletonCount }).map((_, i) => (
              <P.SkeletonCard key={`product-skeleton-${i}`}>
                <P.SkeletonMedia />
                <P.SkeletonBody>
                  <P.SkeletonLine $width={70} $height={18} />
                  <P.SkeletonLine $width={40} $height={12} />
                  <P.SkeletonLine $width={90} $height={10} />
                  <P.SkeletonLine $width={60} $height={10} />
                </P.SkeletonBody>
              </P.SkeletonCard>
            ))}
          </P.Grid>
        ) : filtered.length === 0 ? (
          <P.EmptyState>
            <P.EmptyIcon>
              <FiInbox aria-hidden="true" />
            </P.EmptyIcon>
            <P.EmptyTitle>
              {t("products.empty_title", { defaultValue: "No products found" })}
            </P.EmptyTitle>
            <P.EmptyCopy>
              {hasActiveFilters
                ? t("products.empty_search", {
                    defaultValue:
                      "Try a different search term or clear your filters.",
                  })
                : t("products.empty_browse", {
                    defaultValue:
                      "Check back soon for fresh produce from our farms!",
                  })}
            </P.EmptyCopy>
            {hasActiveFilters ? (
              <P.CardCta type="button" onClick={handleResetAll}>
                {t("products.clear_filters", { defaultValue: "Clear all filters" })}
              </P.CardCta>
            ) : (
              <P.CardCta type="button" onClick={() => navigate("/farms")}>
                {t("products.browse_farms", { defaultValue: "Browse farms" })}
              </P.CardCta>
            )}
          </P.EmptyState>
        ) : (
          <AnimatePresence mode="popLayout">
            {viewMode === "grid" ? (
              <P.Grid
                key="grid"
                as={motion.div}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                {filtered.map((product, index) => (
                  <ProductCardRedesigned
                    key={product.id}
                    product={product}
                    variant="grid"
                    index={index}
                    onView={() => setSelected(product)}
                    onEdit={setEdit}
                    isFarmer={canEditProducts}
                  />
                ))}
              </P.Grid>
            ) : (
              <P.ListStack
                key="list"
                as={motion.div}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                {filtered.map((product, index) => (
                  <ProductCardRedesigned
                    key={product.id}
                    product={product}
                    variant="list"
                    index={index}
                    onView={() => setSelected(product)}
                    onEdit={setEdit}
                    isFarmer={canEditProducts}
                  />
                ))}
              </P.ListStack>
            )}
          </AnimatePresence>
        )}

        {recentlyViewed.length > 0 ? (
          <P.RecentlyViewedSection>
            <P.RecentlyViewedHeader>
              <P.RecentlyViewedTitle>
                {t("products.recently_viewed", {
                  defaultValue: "Recently viewed",
                })}
              </P.RecentlyViewedTitle>
            </P.RecentlyViewedHeader>
            <P.RecentlyViewedScroll>
              {recentlyViewed.map((item) => (
                <P.RecentlyViewedCard
                  key={item.id}
                  type="button"
                  onClick={() => navigate(`/products/${item.id}`)}
                >
                  <img src={item.image} alt={item.name} loading="lazy" />
                  <P.RecentlyViewedMeta>
                    <p className="name">{item.name}</p>
                    {item.price != null ? (
                      <p className="price">{formatLek(item.price)}</p>
                    ) : null}
                  </P.RecentlyViewedMeta>
                </P.RecentlyViewedCard>
              ))}
            </P.RecentlyViewedScroll>
          </P.RecentlyViewedSection>
        ) : null}
      </P.Container>
      <Footer />

      <ProductDetailModalRedesigned
        product={selected}
        onClose={() => setSelected(null)}
      />

      {edit && (
        <FarmerStockEditor
          product={edit}
          onSave={handleSave}
          onClose={() => setEdit(null)}
        />
      )}
    </P.Page>
  );
};

export default ProductList;
